import { placeParamsToURL } from 'helpers/CommonHelper';
import { lazy, type ReactElement } from 'react';

interface Route {
  path: string;
  element: ReactElement;
  layout?: string;
  routes?: { [key: string]: Route };
}

const HomePage = lazy(() => import(`modules/customer/pages/CustomerHome`));
const ProductDetailPage = lazy(
  () => import(`modules/customer/pages/CustomerProduct/ProductDetail`)
);
const ProductListPage = lazy(() => import(`modules/customer/pages/CustomerProduct`));
const BlogListPage = lazy(() => import(`modules/customer/pages/CustomerBlog`));
const BlogDetailPage = lazy(() => import(`modules/customer/pages/CustomerBlog/CustomerBlogDetail`));
const BlogCategoryListPage = lazy(
  () => import(`modules/customer/pages/CustomerBlog/CustomerBlogCategoryList`)
);
const BlogAuthorListPage = lazy(
  () => import(`modules/customer/pages/CustomerBlog/CustomerBlogAuthorList`)
);
const CartPage = lazy(() => import(`modules/customer/pages/CustomerCart`));
const WishListPage = lazy(() => import(`modules/customer/pages/CustomerWishList`));
const FollowingListPage = lazy(() => import(`modules/customer/pages/CustomerFollowingList`));
const TopFFLPage = lazy(() => import(`modules/customer/pages/CustomerTopFFL`));
const CheckoutPage = lazy(() => import(`modules/customer/pages/CustomerCheckout`));
const FFLListingPage = lazy(() => import(`modules/customer/pages/CustomerFFLListing`));
const FFLDetailPage = lazy(() => import(`modules/customer/pages/CustomerFFLListing/FFLDetail`));
const CustomerSellFirearmsPage = lazy(() => import(`modules/customer/pages/CustomerSellFirearms`));
const CustomerProfilePage = lazy(() => import(`modules/customer/pages/CustomerProfile`));
const CustomerProfileOverviewPage = lazy(
  () => import(`modules/customer/pages/CustomerProfile/CustomerOverview`)
);
const CustomerNotificationPage = lazy(
  () => import(`modules/customer/pages/CustomerProfile/CustomerNotification`)
);
const CustomerTradeInPage = lazy(
  () => import(`modules/customer/pages/CustomerProfile/CustomerTradeIn`)
);
const CustomerOffersPage = lazy(
  () => import(`modules/customer/pages/CustomerProfile/CustomerOffers`)
);
const CustomerComparePage = lazy(
  () => import(`modules/customer/pages/CustomerProfile/CustomerCompare`)
);
const CustomerHelpSupportPage = lazy(
  () => import(`modules/customer/pages/CustomerProfile/CustomerHelpSupport`)
);
const CustomerFAQPage = lazy(() => import(`modules/customer/pages/CustomerFAQ`));
const CustomerContentPage = lazy(() => import(`modules/customer/pages/CustomerContentPage`));
const CustomerOrdersPage = lazy(
  () => import(`modules/customer/pages/CustomerProfile/CustomerOrders`)
);
const CustomerReviewsPage = lazy(
  () => import(`modules/customer/pages/CustomerProfile/CustomerReviews`)
);

// export const customerMainRoutes: { [key: string]: Route } = {
//   resetPassword: {
//     path: `/home`,
//     element: <HomePage />,
//     layout: 'auth',
//   },
// };

export const customerMainRoutes: { [key: string]: Route } = {
  customerHome: {
    path: `/`,
    element: <HomePage />,
  },
  productList: {
    path: `/products`,
    element: <ProductListPage />,
  },
  blogList: {
    path: `/blogs`,
    element: <BlogListPage />,
  },
  blogCategoryList: {
    path: `/blogs/category/:slug?`,
    element: <BlogCategoryListPage />,
  },
  blogAuthorList: {
    path: `/blogs/author/:slug?`,
    element: <BlogAuthorListPage />,
  },
  blogDetails: {
    path: `/blogs/details/:slug`,
    element: <BlogDetailPage />,
  },
  productDetail: {
    path: `/product/:slug`,
    element: <ProductDetailPage />,
  },
  cart: {
    path: `/cart`,
    element: <CartPage />,
  },
  checkout: {
    path: `/cart/checkout`,
    element: <CheckoutPage />,
  },
  wishList: {
    path: `/my-wishlist`,
    element: <WishListPage />,
  },
  followingList: {
    path: `/my-following`,
    element: <FollowingListPage />,
  },
  topFFL: {
    path: `/top-ffl`,
    element: <TopFFLPage />,
  },
  fflListing: {
    path: `/ffl-listing`,
    element: <FFLListingPage />,
  },
  fflDetail: {
    path: `/ffl/:slug`,
    element: <FFLDetailPage />,
  },
  sellFirearms: {
    path: `/sell-your-firearms`,
    element: <CustomerSellFirearmsPage />,
  },
  faq: {
    path: `/faq`,
    element: <CustomerFAQPage />,
  },
  contentPage: {
    path: `/content-page/:slug`,
    element: <CustomerContentPage />,
  },
};
export const customerProfileMain = {
  path: `/profile`,
};
// base path without the tab, used to keep the menu highlighted on every trade-in tab
export const TRADE_IN_BASE_PATH = `${customerProfileMain.path}/my-trade-in`;
export const customerProfileRoutes: { [key: string]: Route } = {
  profileOverview: {
    path: `${customerProfileMain.path}/overview`,
    element: <CustomerProfileOverviewPage />,
  },
  profileGeneralInfo: {
    path: `${customerProfileMain.path}/general-information`,
    element: <CustomerProfilePage />,
  },
  notificationList: {
    path: `${customerProfileMain.path}/notifications`,
    element: <CustomerNotificationPage />,
  },
  followingList: {
    path: `${customerProfileMain.path}/my-following`,
    element: <FollowingListPage />,
  },
  tradeIn: {
    path: `${TRADE_IN_BASE_PATH}/:tab?`,
    element: <CustomerTradeInPage />,
  },
  favoriteList: {
    path: `${customerProfileMain.path}/my-favorite`,
    element: <WishListPage />,
  },
  offersList: {
    path: `${customerProfileMain.path}/offers`,
    element: <CustomerOffersPage />,
  },
  compareList: {
    path: `${customerProfileMain.path}/compare-products`,
    element: <CustomerComparePage />,
  },
  helpAndSupport: {
    path: `${customerProfileMain.path}/help-and-support`,
    element: <CustomerHelpSupportPage />,
  },
  orders: {
    path: `${customerProfileMain.path}/orders`,
    element: <CustomerOrdersPage />,
  },
  reviews: {
    path: `${customerProfileMain.path}/reviews`,
    element: <CustomerReviewsPage />,
  },
};
// auth pages
const CreatePasswordPage = lazy(() => import(`pages/Authentication/CreatePassword`));
export const customerAuthRoutes: { [key: string]: Route } = {
  createPassword: {
    path: `/create-password`,
    element: <CreatePasswordPage />,
  },
};
export const getCustomerrHomePageRoute = (): string => {
  return customerMainRoutes.customerHome.path;
};

export const getCustomerContentPageRoute = (slug: string): string => {
  return placeParamsToURL(customerMainRoutes.contentPage.path, { slug });
};

// absolute link to a content page (customer pages are served by this same app) - for copying
export const getCustomerContentPageURL = (slug: string): string => {
  return `${window.location.origin}${getCustomerContentPageRoute(slug)}`;
};

// trade-in tabs
export const TRADE_IN_TABS = ['active', 'sold'];
export const DEFAULT_TRADE_IN_TAB = 'active';
// tradeIn.path keeps the `:tab?` placeholder for route matching, so always build
// links through this helper to avoid landing on `/profile/my-trade-in/:tab`.
export const getTradeInPath = (tab: string = DEFAULT_TRADE_IN_TAB): string => {
  const selectedTab = TRADE_IN_TABS.includes(tab) ? tab : DEFAULT_TRADE_IN_TAB;
  return placeParamsToURL(customerProfileRoutes.tradeIn.path, { tab: selectedTab });
};
